import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { toast } from "../components/ui/use-toast.js";
import { useAuth } from "./AuthProvider.js";
import { isPublicPath, returnPathOf, setSessionExpiryHandler } from "./sessionExpiry.js";

/**
 * Turns "the session is gone" into a sign-out that keeps the reseller's place.
 *
 * `src/crm/lib/api.ts` reports a 401 from the BFF's CRM passthrough; this is
 * what that report reaches. It renders nothing — it exists to be the one place
 * in the app that has the router, the auth context and the toast store in the
 * same scope, which is precisely what the axios interceptor does not have and
 * why the old code could only manage `window.location.assign('/login')`.
 *
 * Mounted inside `App` rather than `main.tsx` so it is part of the routed tree
 * every test renders, and cannot drift away from the routes it navigates to.
 *
 * Three refusals, and each one is a bug that would otherwise be real:
 *
 *   NOBODY IS SIGNED IN — nothing to lose and nowhere to send them. Signing
 *   out someone already signed out is how a redirect loop starts.
 *
 *   THE PAGE DOES NOT NEED A SESSION — `/register` and friends render for
 *   anyone. Throwing a would-be reseller out of a half-filled application
 *   because a background call 401'd is worse than the 401 was.
 *
 *   A SIGN-OUT IS ALREADY UNDERWAY — a page with six panels raises six 401s
 *   at once. `user` cannot dedupe them: all six handlers run in the same tick,
 *   before React has re-rendered with the cleared context. So the latch is a
 *   ref, which is synchronous, and it lifts when a session exists again.
 */
export function SessionExpiryBridge(): null {
  const { user, applicant, expireSession } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const signingOut = useRef(false);

  // A new session re-arms the latch. Without this the SECOND expiry of a
  // browsing session would be swallowed silently.
  useEffect(() => {
    if (user || applicant) signingOut.current = false;
  }, [user, applicant]);

  useEffect(() => {
    return setSessionExpiryHandler(() => {
      if (!user && !applicant) return;
      if (isPublicPath(location.pathname)) return;
      if (signingOut.current) return;
      signingOut.current = true;

      expireSession();

      // A full page load at least LOOKED like something happened. Arriving on
      // the sign-in card by SPA navigation, mid-task, does not — so say so.
      toast({
        title: "Your session expired",
        description: "Please sign in again to pick up where you left off.",
        severity: "warning",
      });

      // `state.from` is the channel `RequireAuth` already uses and `LoginPage`
      // already reads, so the two agree by construction. `replace` because the
      // dead page must not sit in history for the Back button to return to.
      navigate("/login", { replace: true, state: { from: returnPathOf(location) } });
    });
  }, [user, applicant, expireSession, location, navigate]);

  return null;
}
